import apiClient from "../api/apiClient";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface Room {
  _id: string;
  name: string;
  slug: string;
  description: string | null;
  icon: string | null;
  banner: string | null;
  membersCount: number;
  isOfficial: boolean;
  moderators: string[];
  createdAt: string;
}

export interface RoomFormData {
  name: string;
  description?: string;
  icon?: string;
  banner?: string;
  isOfficial?: boolean;
  moderators?: string[];
}

export interface PaginatedResponse<T> {
  status: boolean;
  data: T[];
  pagination: {
    current_page: number;
    per_page: number;
    total: number;
    total_pages: number;
  };
}

export interface PostListFilters {
  postType?: string;
  search?: string;
  published?: "true" | "false" | "";
  reportedOnly?: "true" | "";
  page?: number;
  limit?: number;
}

export interface AuctionCollectionSummary {
  _id: string;
  name: string;
  slug: string;
  coverImage: string | null;
  isActive: boolean;
}

export interface AdminPostFormData {
  postType: "ARTICLE" | "COLLECTION" | "EVENT";
  title: string;
  content: string;
  excerpt?: string;
  featured_image?: string;
  images?: string[];
  tags?: string[];
  meta_title?: string;
  meta_description?: string;
  published?: boolean;
  // EVENT
  event_date?: string | null;
  event_location?: string | null;
  event_url?: string | null;
  // COLLECTION
  collection_video?: string | null;
  collection_start_date?: string | null;
  collection_end_date?: string | null;
  linkedCollection?: string | null;
}

// ─── Service ──────────────────────────────────────────────────────────────────

export class CommunityAdminService {
  // ── Rooms ────────────────────────────────────────────────────────────────
  static async listRooms(
    search = "",
    page = 1,
    limit = 20,
  ): Promise<PaginatedResponse<Room>> {
    const params = new URLSearchParams({
      search,
      page: String(page),
      limit: String(limit),
    });
    const res = await apiClient.get(`/community/rooms/admin-list?${params}`);
    return res.data;
  }

  static async createRoom(data: RoomFormData) {
    const res = await apiClient.post("/community/rooms", data);
    return res.data;
  }

  static async updateRoom(id: string, data: Partial<RoomFormData>) {
    const res = await apiClient.put(`/community/rooms/${id}`, data);
    return res.data;
  }

  static async deleteRoom(id: string) {
    const res = await apiClient.delete(`/community/rooms/${id}`);
    return res.data;
  }

  // ── Posts (all types, moderation view) ──────────────────────────────────
  static async listPosts(
    filters: PostListFilters,
  ): Promise<PaginatedResponse<any>> {
    const params = new URLSearchParams();
    if (filters.postType) params.append("postType", filters.postType);
    if (filters.search) params.append("search", filters.search);
    if (filters.published) params.append("published", filters.published);
    if (filters.reportedOnly)
      params.append("reportedOnly", filters.reportedOnly);
    params.append("page", String(filters.page || 1));
    params.append("limit", String(filters.limit || 20));

    const res = await apiClient.get(`/admin/community/posts?${params}`);
    return res.data;
  }

  static async getPostDetail(id: string) {
    const res = await apiClient.get(`/admin/community/posts/${id}`);
    return res.data;
  }

  static async updatePost(id: string, data: Record<string, any>) {
    const res = await apiClient.put(`/admin/community/posts/${id}`, data);
    return res.data;
  }

  static async deletePost(id: string) {
    const res = await apiClient.delete(`/admin/community/posts/${id}`);
    return res.data;
  }

  static async removePost(id: string, reason?: string) {
    const res = await apiClient.put(`/admin/community/posts/${id}/remove`, {
      reason,
    });
    return res.data;
  }

  static async restorePost(id: string) {
    const res = await apiClient.put(`/admin/community/posts/${id}/restore`);
    return res.data;
  }

  // ── Create (admin-authored posts: Article / Collection / Event) ─────────────
  static async createPost(data: AdminPostFormData) {
    const res = await apiClient.post("/community/posts", data);
    return res.data;
  }

  // ── Pin / Unpin (top-of-feed, 1-7 days) ──────────────────────────────────
  static async pinPost(id: string, days: number) {
    const res = await apiClient.put(`/admin/community/posts/${id}/pin`, {
      days,
    });
    return res.data;
  }

  static async unpinPost(id: string) {
    const res = await apiClient.put(`/admin/community/posts/${id}/unpin`);
    return res.data;
  }

  // ── Auction collections (for the "link a collection" picker) ────────────
  static async searchAuctionCollections(
    search = "",
    limit = 8,
  ): Promise<{ status: boolean; data: AuctionCollectionSummary[] }> {
    const params = new URLSearchParams({
      search,
      limit: String(limit),
    });
    const res = await apiClient.get(`/collections?${params}`);
    return res.data;
  }

  // ── Reports ───────────────────────────────────────────────────────────────
  static async listReportedPosts(
    page = 1,
    limit = 20,
  ): Promise<PaginatedResponse<any>> {
    const params = new URLSearchParams({
      page: String(page),
      limit: String(limit),
    });
    const res = await apiClient.get(`/admin/community/reports?${params}`);
    return res.data;
  }

  static async resolveReport(
    postId: string,
    reportId: string,
    action: "remove_post" | "dismiss",
  ) {
    const res = await apiClient.put(
      `/admin/community/posts/${postId}/reports/${reportId}/resolve`,
      { action },
    );
    return res.data;
  }

  static async resolveAllReports(
    postId: string,
    action: "remove_post" | "dismiss",
  ) {
    const res = await apiClient.put(
      `/admin/community/posts/${postId}/reports/resolve-all`,
      { action },
    );
    return res.data;
  }

  // ── Media upload — reuses community's generic media endpoint ───────────────
  static async uploadImage(
    file: File,
  ): Promise<{ status: boolean; imageUrl: string }> {
    const formData = new FormData();
    formData.append("media", file);
    const res = await apiClient.post("/community/post/upload-media", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return { status: res.data.status, imageUrl: res.data.mediaUrl };
  }
}

export default CommunityAdminService;
